import { gameKeys, games } from "./games";
import type { CalibrationGameSummary, CalibrationSummary, TicketHistoryRecord } from "./types";

function expectedReturn(item: TicketHistoryRecord): number {
  const ticket = item.recommendation?.tickets?.find((candidate) => candidate.ticket.compact === item.compact) ?? item.recommendation?.tickets?.[0];
  return Number(ticket?.lower_ev ?? 0) + Number(ticket?.jackpot_ev ?? 0);
}

function expectedAnyPrize(item: TicketHistoryRecord): number {
  return Number(item.recommendation?.portfolio_any_prize_probability_approx ?? item.recommendation?.any_prize_probability ?? 0);
}

function actualReturn(item: TicketHistoryRecord): number {
  return Number(item.result?.estimated_prize_cash ?? 0);
}

function checked(history: TicketHistoryRecord[]): TicketHistoryRecord[] {
  return history.filter((item) => (item.lifecycle_status === "checked" || item.status === "draw_complete") && item.result);
}

function sampleStatus(n: number): string {
  if (n >= 250) return "Large enough to judge calibration direction, still not enough to judge jackpot skill.";
  if (n >= 50) return "Enough checked tickets to start reading trends, still noisy.";
  if (n > 0) return "Early record. Useful for audit, not enough for a strong grade.";
  return "No checked tickets yet.";
}

function normalPrizeBand(expected: number, rows: number): { low: number; high: number } {
  const variance = Math.max(expected * Math.max(0, 1 - expected / Math.max(rows, 1)), 0);
  const margin = 2 * Math.sqrt(variance);
  return {
    low: Math.max(0, Math.floor(expected - margin)),
    high: Math.min(rows, Math.ceil(expected + margin))
  };
}

function gameSummary(gameKey: keyof typeof games, rows: TicketHistoryRecord[]): CalibrationGameSummary {
  const gameRows = checked(rows.filter((item) => item.game_key === gameKey));
  const expectedPrizes = gameRows.reduce((sum, item) => sum + expectedAnyPrize(item), 0);
  const actualPrizes = gameRows.filter((item) => item.result?.prize_name).length;
  const expected = gameRows.reduce((sum, item) => sum + expectedReturn(item), 0);
  const actual = gameRows.reduce((sum, item) => sum + actualReturn(item), 0);
  const note =
    gameRows.length === 0
      ? "No checked tickets yet."
      : actualPrizes >= expectedPrizes
        ? "Actual prizes are at or above the tiny expected count so far."
        : "Actual prizes are below expectation so far; sample size matters.";
  return {
    game_key: gameKey,
    game_name: games[gameKey].name,
    checked_tickets: gameRows.length,
    expected_any_prizes: expectedPrizes,
    actual_prizes: actualPrizes,
    expected_return: expected,
    actual_return: actual,
    calibration_note: note
  };
}

export function buildCalibrationSummary(history: TicketHistoryRecord[], now = new Date()): CalibrationSummary {
  const rows = checked(history);
  const expectedPrizes = rows.reduce((sum, item) => sum + expectedAnyPrize(item), 0);
  const actualPrizes = rows.filter((item) => item.result?.prize_name).length;
  const expected = rows.reduce((sum, item) => sum + expectedReturn(item), 0);
  const actual = rows.reduce((sum, item) => sum + actualReturn(item), 0);
  const band = normalPrizeBand(expectedPrizes, rows.length);
  return {
    generated_at: now.toISOString(),
    checked_tickets: rows.length,
    expected_any_prizes: expectedPrizes,
    actual_prizes: actualPrizes,
    expected_return: expected,
    actual_return: actual,
    return_gap: actual - expected,
    normal_low_prize_tickets: band.low,
    normal_high_prize_tickets: band.high,
    actual_prize_rate: rows.length ? actualPrizes / rows.length : 0,
    sample_status: sampleStatus(rows.length),
    by_game: gameKeys.map((gameKey) => gameSummary(gameKey, history))
  };
}
